import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import { getPublicImageUrl } from '../../utils/storage';
import * as FiIcons from 'react-icons/fi';

const {
  FiMail,
  FiList,
  FiSend,
  FiFileText,
  FiMessageCircle,
  FiBarChart2,
  FiLink,
  FiLogOut,
  FiUser,
  FiChevronDown,
  FiChevronRight,
  FiClipboard,
} = FiIcons;

const MarketingSidebar = () => {
  const { profile, role, displayName, logout } = useAuth();
  const navigate = useNavigate();
  const [emailOpen, setEmailOpen] = useState(true);
  const [formsOpen, setFormsOpen] = useState(true);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 bg-slate-800 text-white p-2 rounded-lg shadow-lg"
      >
        <SafeIcon icon={isMobileOpen ? FiChevronRight : FiChevronDown} className="w-6 h-6" />
      </button>
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      <div
        className={`${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-40 w-72 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 border-r border-slate-700/50 flex flex-col transition-transform duration-300`}
      >
        <div className="p-6 border-b border-slate-700/50">
          <div className="flex items-center space-x-3 mb-2">
            <div className="relative">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-xl">M</span>
              </div>
            </div>
            <div>
              <h1 className="text-lg font-bold text-white leading-none" style={{ fontFamily: 'Dancing Script, cursive' }}>
                Marketing
              </h1>
              <p className="text-xs text-slate-400 mt-0.5">Prosperity Leaders</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {/* Email section */}
          <div>
            <button
              onClick={() => setEmailOpen(!emailOpen)}
              className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-slate-300 hover:bg-slate-700/50 hover:text-white transition-all group"
            >
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiMail} className="w-5 h-5 text-slate-400 group-hover:text-emerald-400" />
                <span className="font-medium">Email</span>
              </div>
              <SafeIcon icon={emailOpen ? FiChevronDown : FiChevronRight} className="w-4 h-4 text-slate-400" />
            </button>
            {emailOpen && (
              <div className="ml-4 mt-1 space-y-0.5 border-l border-slate-700 pl-3">
                <NavLink
                  to="/marketing/email/templates"
                  onClick={() => setIsMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all ${
                      isActive ? 'bg-emerald-500/20 text-emerald-300' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                    }`
                  }
                >
                  <SafeIcon icon={FiFileText} className="w-4 h-4" />
                  <span className="text-sm font-medium">Templates</span>
                </NavLink>
                <NavLink
                  to="/marketing/email/workflows"
                  onClick={() => setIsMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all ${
                      isActive ? 'bg-emerald-500/20 text-emerald-300' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                    }`
                  }
                >
                  <SafeIcon icon={FiList} className="w-4 h-4" />
                  <span className="text-sm font-medium">Workflows</span>
                </NavLink>
                <NavLink
                  to="/marketing/email/send"
                  onClick={() => setIsMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all ${
                      isActive ? 'bg-emerald-500/20 text-emerald-300' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                    }`
                  }
                >
                  <SafeIcon icon={FiSend} className="w-4 h-4" />
                  <span className="text-sm font-medium">Quick Send</span>
                </NavLink>
                <NavLink
                  to="/marketing/email/logs"
                  onClick={() => setIsMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all ${
                      isActive ? 'bg-emerald-500/20 text-emerald-300' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                    }`
                  }
                >
                  <SafeIcon icon={FiFileText} className="w-4 h-4" />
                  <span className="text-sm font-medium">Logs</span>
                </NavLink>
              </div>
            )}
          </div>

          {/* Forms section */}
          <div className="pt-2">
            <button
              onClick={() => setFormsOpen(!formsOpen)}
              className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-slate-300 hover:bg-slate-700/50 hover:text-white transition-all group"
            >
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiClipboard} className="w-5 h-5 text-slate-400 group-hover:text-emerald-400" />
                <span className="font-medium">Forms</span>
              </div>
              <SafeIcon icon={formsOpen ? FiChevronDown : FiChevronRight} className="w-4 h-4 text-slate-400" />
            </button>
            {formsOpen && (
              <div className="ml-4 mt-1 space-y-0.5 border-l border-slate-700 pl-3">
                <NavLink
                  to="/marketing/forms"
                  end
                  onClick={() => setIsMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all ${
                      isActive ? 'bg-emerald-500/20 text-emerald-300' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                    }`
                  }
                >
                  <SafeIcon icon={FiClipboard} className="w-4 h-4" />
                  <span className="text-sm font-medium">All Forms</span>
                </NavLink>
              </div>
            )}
          </div>

          {/* Placeholder sections */}
          <div className="pt-4 border-t border-slate-700/50 space-y-0.5">
            <div className="flex items-center space-x-3 px-4 py-3 rounded-xl text-slate-500 cursor-not-allowed">
              <SafeIcon icon={FiMessageCircle} className="w-5 h-5" />
              <span className="font-medium text-sm">SMS</span>
              <span className="text-xs text-slate-600">(Coming soon)</span>
            </div>
            <div className="flex items-center space-x-3 px-4 py-3 rounded-xl text-slate-500 cursor-not-allowed">
              <SafeIcon icon={FiBarChart2} className="w-5 h-5" />
              <span className="font-medium text-sm">Analytics</span>
              <span className="text-xs text-slate-600">(Coming soon)</span>
            </div>
            <div className="flex items-center space-x-3 px-4 py-3 rounded-xl text-slate-500 cursor-not-allowed">
              <SafeIcon icon={FiLink} className="w-5 h-5" />
              <span className="font-medium text-sm">Invitation Links</span>
              <span className="text-xs text-slate-600">(Coming soon)</span>
            </div>
          </div>
        </nav>

        <div className="p-4 border-t border-slate-700/50">
          <NavLink
            to="/dashboard"
            className="block bg-slate-700/30 rounded-xl p-4 mb-3 hover:bg-slate-700/50 transition-colors cursor-pointer group"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg group-hover:ring-2 group-hover:ring-emerald-500/50 overflow-hidden bg-gradient-to-br from-emerald-500 to-teal-500">
                {getPublicImageUrl(profile?.profile_image_url, 'profile-images') ? (
                  <img src={getPublicImageUrl(profile.profile_image_url, 'profile-images')} alt="" className="w-full h-full object-cover" />
                ) : (
                  <SafeIcon icon={FiUser} className="w-5 h-5 text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{displayName}</p>
                <p className="text-xs text-slate-400 capitalize">Back to Dashboard</p>
              </div>
            </div>
          </NavLink>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 px-4 py-3 w-full text-left text-slate-300 hover:bg-slate-700/50 hover:text-white rounded-xl transition-all group"
          >
            <SafeIcon icon={FiLogOut} className="w-5 h-5 text-slate-400 group-hover:text-red-400 transition-colors" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default MarketingSidebar;
